import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * RegulatoryChip — the persistent provenance pill in every topbar.
 *
 * Shows where you stand in the SIRA → SSP → SSU → Site → build hierarchy.
 * It is read-only: a plain element with no button role, chevron or
 * expanded state, because no surface opens anything from it.
 */
export interface RegulatoryChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  scope: {
    tier: 'sira' | 'ssp' | 'ssu' | 'site' | 'platform';
    /** SSP code, eg "DXB-SECURITY". */
    ssp?: string;
    /** SSU code, eg "MALL-OF-EMIRATES". */
    ssu?: string;
    /** Site code, eg "TOWER-A". */
    site?: string;
  };
  /**
   * Build identifier, eg "v1.4.2-pre.7+f8a3c1". The segment is hidden when
   * it is unset, "unknown" or "pending".
   */
  build?: string;
  /** Optional secondary state — "live", "degraded", "offline". */
  status?: 'live' | 'degraded' | 'offline' | 'unknown';
  /** Keep the chip on one line (narrow topbars clip instead of wrapping). */
  nowrap?: boolean;
}

const STATUS_DOT: Record<NonNullable<RegulatoryChipProps['status']>, string> = {
  live: 'bg-[var(--color-emerald-500)]',
  degraded: 'bg-[var(--color-amber-500)]',
  offline: 'bg-[var(--color-red-500)]',
  unknown: 'bg-[var(--color-graphite-500)]',
};

/** A provenance value worth printing: set, and not a placeholder. */
function provenanceValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && !/^(unknown|pending)$/i.test(trimmed) ? trimmed : undefined;
}

const TIER_LABEL: Record<RegulatoryChipProps['scope']['tier'], string> = {
  sira: 'SIRA',
  ssp: 'SSP',
  ssu: 'SSU',
  site: 'SITE',
  platform: 'PLATFORM',
};

export const RegulatoryChip = React.forwardRef<HTMLSpanElement, RegulatoryChipProps>(
  ({ scope, build, status = 'live', nowrap = false, className, ...rest }, ref) => {
    // Build the breadcrumb path. Each segment is monospace, dot-separated.
    const segments: Array<{ label: string; value: string }> = [];
    segments.push({ label: 'TIER', value: TIER_LABEL[scope.tier] });
    if (scope.ssp) segments.push({ label: 'SSP', value: scope.ssp });
    if (scope.ssu) segments.push({ label: 'SSU', value: scope.ssu });
    if (scope.site) segments.push({ label: 'SITE', value: scope.site });
    const buildValue = provenanceValue(build);
    const wrap = nowrap ? 'flex-nowrap whitespace-nowrap' : 'flex-wrap';

    return (
      <span
        ref={ref}
        className={cn(
          // Wraps by default so a narrow viewport never scrolls sideways; min-h-9
          // and max-w-full keep it inside the topbar row.
          'inline-flex items-center gap-x-2 gap-y-0.5 min-h-9 max-w-full ps-2.5 pe-2.5 rounded-sm',
          wrap,
          'border border-[var(--color-hairline)] bg-[var(--color-surface)]',
          'text-[var(--color-foreground-2)]',
          className,
        )}
        {...rest}
      >
        <span
          role="img"
          aria-label={`status ${status}`}
          className={cn('inline-block size-1.5 rounded-full shrink-0', STATUS_DOT[status])}
        />

        <span
          className={cn(
            'flex items-center gap-x-1.5 gap-y-0.5 font-mono text-[0.6875rem] tracking-wide tabular-nums',
            wrap,
          )}
        >
          {segments.map((seg, i) => (
            <React.Fragment key={`${seg.label}-${i}`}>
              {i > 0 && <span className="text-[var(--color-foreground-mute)] select-none">·</span>}
              <span className="text-[var(--color-foreground-mute)] uppercase">{seg.label}</span>
              <span className="text-[var(--color-foreground)] uppercase">{seg.value}</span>
            </React.Fragment>
          ))}
        </span>

        {buildValue ? (
          <>
            <span className="self-stretch w-px bg-[var(--color-hairline)]" aria-hidden />
            <span className="font-mono text-[0.6875rem] text-[var(--color-foreground-3)] tracking-wide tabular-nums">
              {buildValue}
            </span>
          </>
        ) : null}
      </span>
    );
  },
);
RegulatoryChip.displayName = 'RegulatoryChip';

/**
 * ProvenanceFooter — the small monospace strip pinned to the bottom of every
 * surface. Selectable and copyable. It prints only real provenance: a field
 * that is unset, "unknown" or "pending" is left out, and the strip renders
 * nothing when no field is left. Labels come translated from the app
 * (`useProvenanceLabels()` in @cguard/i18n); English defaults cover stories
 * and tests.
 */
export interface ProvenanceFooterLabels {
  build: string;
  region: string;
  environment: string;
  serverTime: string;
  signedBy: string;
  cert: string;
}

const defaultProvenanceLabels: ProvenanceFooterLabels = {
  build: 'Build',
  region: 'Region',
  environment: 'Environment',
  serverTime: 'Server time',
  signedBy: 'Signed by',
  cert: 'Certificate',
};

export interface ProvenanceFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  buildHash?: string;
  serverTimeIso?: string;
  region?: string;
  environment?: string;
  signedBy?: string;
  /** Optional cert thumbprint. */
  certThumbprint?: string;
  labels?: Partial<ProvenanceFooterLabels>;
}

export const ProvenanceFooter = React.forwardRef<HTMLDivElement, ProvenanceFooterProps>(
  (
    {
      buildHash,
      serverTimeIso,
      region,
      environment,
      signedBy,
      certThumbprint,
      labels,
      className,
      ...rest
    },
    ref,
  ) => {
    const text = { ...defaultProvenanceLabels, ...labels };
    const fields = (
      [
        ['build', text.build, buildHash],
        ['region', text.region, region],
        ['environment', text.environment, environment],
        ['server-time', text.serverTime, serverTimeIso],
        ['signed-by', text.signedBy, signedBy],
        ['cert', text.cert, certThumbprint],
      ] as const
    ).flatMap(([key, label, raw]) => {
      const value = provenanceValue(raw);
      return value ? [{ key, label, value }] : [];
    });
    if (fields.length === 0) return null;
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-wrap items-baseline gap-x-5 gap-y-1.5 px-4 py-2',
          'border-t border-[var(--color-hairline)] bg-[var(--color-canvas)]',
          'font-mono text-[0.75rem] tracking-[0.06em] tabular-nums',
          className,
        )}
        {...rest}
      >
        {fields.map(({ key, label, value }) => (
          <span key={key} className="inline-flex items-baseline gap-1.5" data-field={key}>
            <span className="text-[var(--color-foreground-mute)]">{label}</span>
            <span className="text-[var(--color-foreground-3)] select-all">{value}</span>
          </span>
        ))}
      </div>
    );
  },
);
ProvenanceFooter.displayName = 'ProvenanceFooter';
